import { useState, type ChangeEvent, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { addDays, format, parseISO, startOfDay } from 'date-fns';
import { resolveWaitingOnLabel, type WaitingOnOption } from '@erp/shared-types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { useToast } from 'src/context/ToastContext';

/** The slice of a record the dialog needs — module-agnostic, so a collection
 * thread's list DTO, its detail DTO, and any other parked record all satisfy
 * it and share this one control. */
export interface SetNextStepRecord {
  id: string;
  /** Readable code shown in the dialog subtitle, e.g. "COL-1024". */
  referenceCode: string;
  title: string;
  /** Optional middle segment of the subtitle (AR: the customer name). */
  subtitle?: string | null;
  waitingOnKey: string | null;
  followUpAt: string | null;
}

/** What the caller persists when the form is saved. */
export interface SetNextStepValues {
  waitingOnKey: string | null;
  /** ISO timestamp, or null to clear the follow-up. */
  followUpAt: string | null;
  /** Trimmed note, omitted when blank. */
  note?: string;
}

/** Sentinel for "no reason" in the Select (Radix disallows empty item values). */
const NONE_VALUE = '__none__';

interface SetNextStepDialogProps {
  /** The record being parked; null keeps the dialog closed. */
  record: SetNextStepRecord | null;
  /** The owning module's waiting-on options, including disabled ones. */
  options: WaitingOnOption[];
  /** Persists the values. Rejecting surfaces the error in the toast. */
  onSave: (values: SetNextStepValues) => Promise<unknown>;
  /** Disables the save button while the caller's mutation is in flight. */
  isSaving: boolean;
  /**
   * Help text under the note field, naming where the note ends up — the one
   * module-specific string in this dialog. Omitted, it reads as AR's
   * collection-thread wording, which is where the dialog started.
   */
  noteHint?: ReactNode;
  onOpenChange: (open: boolean) => void;
}

/**
 * The single "set next step" control: waiting-on reason + follow-up date
 * (+ optional time) + note, captured together so recording a pass stays one
 * interaction. Picking a reason pre-fills the date from its
 * `defaultFollowUpDays`; the date stays editable after. Saving writes all
 * three atomically and is deliberately quiet at write time (no watcher
 * notifications) — the follow-up date instead pulls the record back into the
 * reminder digest once it comes due.
 *
 * Module-agnostic: the options and the persistence are the caller's (see
 * `CollectionThreadSetNextStepDialog` for the AR binding), so nothing here
 * assumes a collection thread.
 */
export function SetNextStepDialog({
  record,
  options,
  onSave,
  isSaving,
  noteHint,
  onOpenChange,
}: SetNextStepDialogProps) {
  return (
    <Dialog open={record !== null} onOpenChange={onOpenChange}>
      {record && (
        // Keyed by record so the form state resets when the target changes
        // (the dialog instance is shared by every row of the list).
        <SetNextStepForm
          key={record.id}
          record={record}
          options={options}
          onSave={onSave}
          isSaving={isSaving}
          noteHint={noteHint}
          onClose={() => onOpenChange(false)}
        />
      )}
    </Dialog>
  );
}

function SetNextStepForm({
  record,
  options,
  onSave,
  isSaving,
  noteHint,
  onClose,
}: {
  record: SetNextStepRecord;
  options: WaitingOnOption[];
  onSave: (values: SetNextStepValues) => Promise<unknown>;
  isSaving: boolean;
  noteHint?: ReactNode;
  onClose: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();

  const [waitingOnKey, setWaitingOnKey] = useState<string | null>(
    record.waitingOnKey,
  );
  const [followUpAt, setFollowUpAt] = useState<Date | undefined>(
    record.followUpAt ? parseISO(record.followUpAt) : undefined,
  );
  const [note, setNote] = useState('');
  // True only while the date shows a value this dialog derived from the chosen
  // reason — a manual edit or a persisted date isn't "pre-filled", so the hint
  // below the picker shouldn't claim it is.
  const [followUpPrefilled, setFollowUpPrefilled] = useState(false);

  // Enabled options only in the picker — plus the record's current key when it
  // has since been disabled/removed, so the current value stays representable
  // (it renders via the same resolve-with-fallback the list uses).
  const pickerOptions = options.filter(
    (option) => option.enabled || option.key === record.waitingOnKey,
  );
  // A key that no longer exists in the config at all has no row in `options`;
  // synthesize one so the controlled Select can still display it.
  const missingCurrentKey =
    record.waitingOnKey !== null &&
    !options.some((option) => option.key === record.waitingOnKey)
      ? record.waitingOnKey
      : null;

  // The DatePicker hands back midnight dates, so a date change must carry the
  // previously chosen time-of-day forward instead of silently resetting it.
  function handleFollowUpDateChange(date: Date | undefined) {
    if (date && followUpAt) {
      const merged = new Date(date);
      merged.setHours(followUpAt.getHours(), followUpAt.getMinutes(), 0, 0);
      setFollowUpAt(merged);
    } else {
      setFollowUpAt(date);
    }
    setFollowUpPrefilled(false);
  }

  // Time-of-day is optional; midnight means "any time that day" (the same
  // value a date-only pick produces), so clearing the input resets to it.
  function handleFollowUpTimeChange(event: ChangeEvent<HTMLInputElement>) {
    if (!followUpAt) return;
    const next = new Date(followUpAt);
    const [hours, minutes] = event.target.value.split(':').map(Number);
    if (Number.isFinite(hours) && Number.isFinite(minutes)) {
      next.setHours(hours, minutes, 0, 0);
    } else {
      next.setHours(0, 0, 0, 0);
    }
    setFollowUpAt(next);
    setFollowUpPrefilled(false);
  }

  const followUpHasTime =
    followUpAt != null &&
    (followUpAt.getHours() !== 0 || followUpAt.getMinutes() !== 0);

  function handleReasonChange(value: string) {
    const nextKey = value === NONE_VALUE ? null : value;
    setWaitingOnKey(nextKey);
    // Pre-fill the follow-up from the reason's default so the common case is
    // two clicks and a sentence. Always overwrite on reason change — the
    // date remains editable after.
    const option = nextKey
      ? options.find((candidate) => candidate.key === nextKey)
      : undefined;
    if (option?.defaultFollowUpDays != null) {
      setFollowUpAt(
        addDays(startOfDay(new Date()), option.defaultFollowUpDays),
      );
      setFollowUpPrefilled(true);
    } else if (nextKey === null) {
      setFollowUpAt(undefined);
      setFollowUpPrefilled(false);
    }
  }

  async function handleSave() {
    try {
      await onSave({
        waitingOnKey,
        followUpAt: followUpAt ? followUpAt.toISOString() : null,
        note: note.trim() || undefined,
      });
      showToast({
        title: gt('Next step saved', {
          context: 'toast title, collection thread next step recorded',
        }),
      });
      onClose();
    } catch (err) {
      showToast({
        title: gt('Could not save next step', {
          context: 'toast title, collection thread next step failed',
        }),
        message:
          err instanceof Error
            ? err.message
            : gt('Something went wrong. Try again.', {
                context: 'toast message, collection thread next step failed',
              }),
        color: 'error',
      });
    }
  }

  return (
    <DialogContent className="sm:max-w-[440px]">
      <DialogHeader>
        <DialogTitle>
          <T context="dialog title, record a collection thread's next step">
            Set next step
          </T>
        </DialogTitle>
        {/* line-clamp, not truncate: truncate's nowrap makes the text's full
            single-line width the description's intrinsic min-content width,
            which inflates DialogContent's grid track past the dialog's own
            max-width and spills the whole form outside the panel on long
            titles. Clamping wraps for sizing but still renders one
            ellipsized line. */}
        <DialogDescription className="line-clamp-1 break-words">
          {record.referenceCode}
          {record.subtitle ? ` · ${record.subtitle}` : ''} · {record.title}
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-1">
        <div className="grid gap-1.5">
          <Label htmlFor="next-step-waiting-on">
            <T context="field label, why the collection thread is parked">
              Waiting on
            </T>
          </Label>
          <Select
            value={waitingOnKey ?? NONE_VALUE}
            onValueChange={handleReasonChange}
          >
            <SelectTrigger id="next-step-waiting-on" className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>
                <span className="text-muted-foreground">
                  <T context="select option, thread parked under no reason">
                    None — needs triage
                  </T>
                </span>
              </SelectItem>
              {pickerOptions.map((option) => (
                <SelectItem key={option.key} value={option.key}>
                  {resolveWaitingOnLabel(option.key, options)}
                </SelectItem>
              ))}
              {missingCurrentKey !== null && (
                <SelectItem value={missingCurrentKey}>
                  {resolveWaitingOnLabel(missingCurrentKey, options)}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="next-step-follow-up">
            <T context="field label, when to revisit the collection thread">
              Follow up on
            </T>
          </Label>
          <div className="flex items-center gap-2">
            <DatePicker
              id="next-step-follow-up"
              value={followUpAt}
              onChange={handleFollowUpDateChange}
              className="w-[160px]"
            />
            <Input
              type="time"
              value={followUpHasTime ? format(followUpAt, 'HH:mm') : ''}
              onChange={handleFollowUpTimeChange}
              disabled={!followUpAt}
              className="h-8 w-[130px] text-sm"
              aria-label={gt('Follow-up time', {
                context: 'time-of-day input next to the follow-up date picker',
              })}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            <T context="hint under the follow-up date + time inputs">
              Time is optional — leave it blank to follow up any time that day.
            </T>
          </p>
          {followUpPrefilled && (
            <p className="text-xs text-muted-foreground">
              <T context="hint, follow-up date was pre-filled from the chosen reason">
                Pre-filled from the reason you picked — adjust as needed.
              </T>
            </p>
          )}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="next-step-note">
            <T context="field label, note about the thread's next step">Note</T>
          </Label>
          <Textarea
            id="next-step-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            maxLength={2000}
            placeholder={gt('What did you decide? Shows in the list.', {
              context: 'placeholder, collection thread next step note',
            })}
          />
          <p className="text-xs text-muted-foreground">
            {noteHint ?? (
              <T context="hint under the next-step note field">
                Shows in the list and the thread timeline. Doesn't notify
                watchers.
              </T>
            )}
          </p>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" size="sm" onClick={onClose}>
          <T context="button, cancel dialog">Cancel</T>
        </Button>
        <Button size="sm" onClick={handleSave} disabled={isSaving}>
          <T context="button, save the collection thread next step">
            Save next step
          </T>
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
